"use client";

import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import {
  HEADER_TAKEOVER,
  headerFirstFrameScript,
  headerStateAttributes,
  headerStateVars,
  initialHeaderFrame,
  watchHeaderState,
  type HeaderFrame,
  type HeaderSettings,
} from "@/utils/header-state";

export interface HeaderView {
  settings: HeaderSettings;
  frame: HeaderFrame;
}

export const HeaderStateContext = createContext<HeaderView | undefined>(
  undefined,
);

/** The look-system header state, or undefined where the header still follows the scroll itself (old websites). */
export const useHeaderState = () => useContext(HeaderStateContext);

/**
 * One scroll state for every header type on the look system. Its wrapper takes no box; after hydration the attributes
 * and values on it are written by watchHeaderState only, so React renders them once, for the server's first frame.
 * Before hydration headerFirstFrameScript writes them, and the header's data-isvisible, for a restored scroll
 * position, which is why the wrapper and the header's state elements suppress the hydration warning.
 */
export default function HeaderStateController({
  settings,
  children,
}: {
  settings: HeaderSettings;
  children: ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState(() => initialHeaderFrame(settings));
  const [first] = useState(() => ({
    attributes: headerStateAttributes(settings, frame),
    style: {
      display: "contents",
      ...headerStateVars(settings, frame),
    } as CSSProperties,
    script: headerFirstFrameScript(settings),
  }));
  const key = JSON.stringify(settings);
  useLayoutEffect(() => {
    if (!root.current) return;
    root.current.dispatchEvent(new Event(HEADER_TAKEOVER));
    // The wrapper's state is written in the controller's animation frame and the header's own data-isvisible by React,
    // which would otherwise render after that frame is painted: one frame of the top look over a switched wrapper.
    let watching = false;
    const stop = watchHeaderState(
      window,
      root.current,
      JSON.parse(key),
      (next) => (watching ? flushSync(() => setFrame(next)) : setFrame(next)),
    );
    watching = true;
    return stop;
  }, [key]);
  return (
    <HeaderStateContext.Provider value={{ settings, frame }}>
      <div
        ref={root}
        {...first.attributes}
        style={first.style}
        suppressHydrationWarning
      >
        {children}
      </div>
      <script dangerouslySetInnerHTML={{ __html: first.script }} />
    </HeaderStateContext.Provider>
  );
}
